import React from 'react';
import { Paper, BottomNavigation, BottomNavigationAction, Badge } from '@mui/material';
import {
  Dashboard, Class as ClassIcon, FactCheck, EventNote,
  Notifications as NotificationsIcon, ChildCare, QuestionAnswer,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useNotifications } from '@/context/NotificationContext';

// Shared with AppLayout.js (bottom padding) and Chatbot.js (FAB clearance) —
// import this instead of re-declaring the role list so the three can't drift apart.
export const BOTTOM_NAV_ROLES = ['teacher', 'parent'];

// Teacher has a real notifications feature (/teacher/notifications, backed by
// teacherAPI.getNotifications), so it gets a 5th "Alerts" tab with a badge.
// Parent has NO notifications feature anywhere in the app today (no route, no
// backend endpoint, NotificationContext.getAPI() returns null for 'parent') —
// so the parent nav only has 4 tabs. Don't add a 5th tab pointing at nothing.
const TEACHER_ITEMS = [
  { label: 'Home', icon: Dashboard, to: '/teacher/dashboard' },
  { label: 'Classes', icon: ClassIcon, to: '/teacher/classes' },
  { label: 'Attendance', icon: FactCheck, to: '/teacher/attendance' },
  { label: 'Meetings', icon: EventNote, to: '/teacher/meetings' },
  { label: 'Alerts', icon: NotificationsIcon, to: '/teacher/notifications', badge: true },
];

export default function MobileBottomNav() {
  const { user, isFeatureEnabled } = useAuth();
  const { unreadCount } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();

  let items = null;
  if (user?.role === 'teacher') {
    items = TEACHER_ITEMS;
  } else if (user?.role === 'parent') {
    items = [
      { label: 'Home', icon: Dashboard, to: '/parent/dashboard' },
      { label: 'Meetings', icon: EventNote, to: '/parent/meetings' },
      isFeatureEnabled('care_notes') && { label: 'Care', icon: ChildCare, to: '/parent/care-notes' },
      isFeatureEnabled('parent_queries') && { label: 'Queries', icon: QuestionAnswer, to: '/parent/queries' },
    ].filter(Boolean);
  }

  if (!items || items.length === 0) return null; // principal/super_admin/school_admin => no bottom nav

  const currentIndex = items.findIndex(i => location.pathname.startsWith(i.to));

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: (t) => t.zIndex.appBar,
        display: { xs: 'block', md: 'none' }, // mobile only
        borderTop: '1px solid', borderColor: 'grey.200',
        pb: 'env(safe-area-inset-bottom)', // iOS notch safe area
      }}
    >
      <BottomNavigation
        value={currentIndex === -1 ? 0 : currentIndex}
        onChange={(_, idx) => navigate(items[idx].to)}
        showLabels
      >
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <BottomNavigationAction
              key={item.to}
              label={item.label}
              sx={{ minWidth: 0, py: 1 }} // keeps tap target tall enough
              icon={
                item.badge && unreadCount > 0
                  ? <Badge color="error" badgeContent={unreadCount}><Icon /></Badge>
                  : <Icon />
              }
            />
          );
        })}
      </BottomNavigation>
    </Paper>
  );
}
